import React, { useState, useEffect } from 'react';
import { rulebookService } from '../../services/rulebookService';
import { logsService } from '../../services/logsService';
import { useAdminAuth } from '../../context/AdminAuthContext';
import RichTextEditor from './RichTextEditor';
import '../../styles/RulebookBlockBuilder.css';

const RulebookBlockBuilder = () => {
  const { user } = useAdminAuth();
  const [edition, setEdition] = useState('');
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const blockTypes = [
    { value: 'section', label: 'Section' },
    { value: 'subsection', label: 'Subsection' },
    { value: 'content', label: 'Content Block' },
    { value: 'rules', label: 'Rules List' },
    { value: 'table', label: 'Table' }
  ];

  useEffect(() => {
    loadRulebook();
  }, []);

  const loadRulebook = async () => {
    setLoading(true);
    try {
      const data = await rulebookService.getRulebook();
      if (data) {
        let blocks = [];
        if (data.blocks) {
          if (typeof data.blocks === 'string') {
            blocks = JSON.parse(data.blocks);
          } else if (Array.isArray(data.blocks)) {
            blocks = data.blocks;
          }
        }
        setBlocks(blocks);
        setEdition(data.edition || '');
      } else {
        setBlocks([]);
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to load rulebook';
      setError(errorMessage);
      console.error('Load rulebook error:', err);
    } finally {
      setLoading(false);
    }
  };

  const addBlock = (type = 'content') => {
    const newBlock = {
      id: Date.now(),
      type,
      title: '',
      content: '',
      order: blocks.length
    };
    setBlocks([...blocks, newBlock]);
    setHasChanges(true);
    setSuccess(null);
  };

  const updateBlock = (id, field, value) => {
    setBlocks(blocks.map(block =>
      block.id === id ? { ...block, [field]: value } : block
    ));
    setHasChanges(true);
    setSuccess(null);
  };

  const deleteBlock = (id) => {
    if (window.confirm('Delete this block?')) {
      setBlocks(blocks.filter(block => block.id !== id));
      setHasChanges(true);
      setSuccess(null);
    }
  };

  const moveBlock = (id, direction) => {
    const index = blocks.findIndex(b => b.id === id);
    if ((direction === 'up' && index > 0) || (direction === 'down' && index < blocks.length - 1)) {
      const newBlocks = [...blocks];
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      [newBlocks[index], newBlocks[swapIndex]] = [newBlocks[swapIndex], newBlocks[index]];
      setBlocks(newBlocks.map((b, i) => ({ ...b, order: i })));
      setHasChanges(true);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!edition.trim()) {
      setError('Edition is required');
      return;
    }

    if (blocks.length === 0) {
      setError('At least one block is required');
      return;
    }

    setSaving(true);
    try {
      const content = renderRulebookHTML();
      const result = await rulebookService.updateRulebook(content, edition, user.id, blocks);

      if (result.success) {
        setHasChanges(false);
        setSuccess('Rulebook saved successfully');

        if (user) {
          await logsService.createLog(
            user.id,
            user.email,
            'RULEBOOK_UPDATE',
            {
              edition: edition,
              block_count: blocks.length
            },
            `Updated rulebook - Edition: ${edition} - ${blocks.length} blocks`
          );
        }
      } else {
        setError(result.error || 'Failed to save rulebook');
      }
    } catch (err) {
      setError(err.message || 'Failed to save rulebook');
    } finally {
      setSaving(false);
    }
  };

  const renderRulebookHTML = () => {
    let html = '';
    const toc = blocks.filter(b => ['section', 'subsection'].includes(b.type) && b.title);

    if (toc.length > 0) {
      html += '<div class="rulebook-toc"><h2>Contents</h2><ul>';
      toc.forEach((block, idx) => {
        const id = `section-${block.id}`;
        if (block.type === 'section') {
          html += `<li><a href="#${id}">${block.title}</a>`;
          const nextBlocks = toc.slice(idx + 1);
          const subsectionItems = [];
          for (let i = 0; i < nextBlocks.length; i++) {
            if (nextBlocks[i].type === 'subsection') {
              subsectionItems.push(nextBlocks[i]);
            } else if (nextBlocks[i].type === 'section') {
              break;
            }
          }
          if (subsectionItems.length > 0) {
            html += '<ul>';
            subsectionItems.forEach(subsection => {
              const subId = `section-${subsection.id}`;
              html += `<li><a href="#${subId}">${subsection.title}</a></li>`;
            });
            html += '</ul>';
          }
          html += '</li>';
        } else if (block.type === 'subsection' && idx === 0) {
          const subId = `section-${block.id}`;
          html += `<li><a href="#${subId}">${block.title}</a></li>`;
        }
      });
      html += '</ul></div>';
    }

    blocks.forEach(block => {
      const id = ['section', 'subsection'].includes(block.type) ? `id="section-${block.id}"` : '';
      
      switch (block.type) {
        case 'section':
          html += `<div class="rulebook-section" ${id}><h2>${block.title}</h2>${block.content}</div>`;
          break;
        case 'subsection':
          html += `<div class="rulebook-subsection" ${id}><h3>${block.title}</h3>${block.content}</div>`;
          break;
        case 'content':
          html += `<div class="rulebook-content-block">${block.content}</div>`;
          break;
        case 'rules':
          html += `<div class="rulebook-rules"><h4>${block.title}</h4>${block.content}</div>`;
          break;
        case 'table':
          html += `<div class="rulebook-table"><h4>${block.title}</h4>${block.content}</div>`;
          break;
        default:
          html += block.content;
      }
    });

    return html;
  };

  const handleReset = () => {
    loadRulebook();
    setHasChanges(false);
  };

  return (
    <div className="rulebook-block-builder">
      <div className="builder-header">
        <h2>Rulebook Manager</h2>
        <div className="header-buttons">
          <button
            className="btn btn-secondary"
            onClick={() => setEditingBlockId(null)}
            title="Preview mode"
          >
            👁️ Preview
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSave} className="rulebook-form">
        <div className="form-group">
          <label htmlFor="edition">Edition / Season</label>
          <input
            id="edition"
            type="text"
            value={edition}
            onChange={(e) => {
              setEdition(e.target.value);
              setHasChanges(true);
            }}
            placeholder="e.g., S17 Edition, Season 18"
            className="form-control"
          />
        </div>

        <div className="builder-container">
          <div className="blocks-section">
            <div className="blocks-header">
              <h3>Rulebook Sections</h3>
              <div className="add-block-buttons">
                {blockTypes.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => addBlock(type.value)}
                    className="btn btn-sm btn-outline"
                    title={`Add ${type.label}`}
                  >
                    + {type.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="blocks-list">
              {blocks.length === 0 ? (
                <div className="empty-state">
                  <p>No blocks yet. Add one to get started.</p>
                </div>
              ) : (
                blocks.map((block, index) => (
                  <div
                    key={block.id}
                    className={`block-card ${['section', 'subsection'].includes(block.type) ? 'is-heading' : ''}`}
                  >
                    <div className="block-header">
                      <div className="block-info">
                        <span className="block-type-badge">{block.type}</span>
                        {block.title && <span className="block-title">{block.title}</span>}
                      </div>
                      <div className="block-actions">
                        <button
                          type="button"
                          onClick={() => moveBlock(block.id, 'up')}
                          disabled={index === 0}
                          className="btn btn-icon"
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveBlock(block.id, 'down')}
                          disabled={index === blocks.length - 1}
                          className="btn btn-icon"
                          title="Move down"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingBlockId(editingBlockId === block.id ? null : block.id)}
                          className="btn btn-icon"
                          title={editingBlockId === block.id ? 'Collapse' : 'Edit'}
                        >
                          {editingBlockId === block.id ? '−' : '+'}
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteBlock(block.id)}
                          className="btn btn-icon btn-danger"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>

                    {editingBlockId === block.id && (
                      <div className="block-editor">
                        <div className="form-group">
                          <label>Block Type</label>
                          <select
                            value={block.type}
                            onChange={(e) => updateBlock(block.id, 'type', e.target.value)}
                            className="form-control"
                          >
                            {blockTypes.map(type => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {['section', 'subsection', 'rules', 'table'].includes(block.type) && (
                          <div className="form-group">
                            <label>Title</label>
                            <input
                              type="text"
                              value={block.title}
                              onChange={(e) => updateBlock(block.id, 'title', e.target.value)}
                              placeholder="Enter title..."
                              className="form-control"
                            />
                          </div>
                        )}

                        <div className="form-group">
                          <label>Content</label>
                          <RichTextEditor
                            value={block.content}
                            onChange={(content) => updateBlock(block.id, 'content', content)}
                            placeholder="Enter block content..."
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="preview-section">
            <div className="preview-header">
              <h3>Live Preview</h3>
            </div>
            <div className="preview-content">
              <div className="rulebook-preview">
                {edition && (
                  <div className="preview-title">
                    <h1>{edition}</h1>
                  </div>
                )}
                <div dangerouslySetInnerHTML={{ __html: renderRulebookHTML() }} />
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            disabled={saving || loading || !hasChanges}
            className="btn btn-primary btn-lg"
          >
            {saving ? 'Saving...' : 'Save Rulebook'}
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={!hasChanges}
            className="btn btn-secondary btn-lg"
          >
            Reset Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default RulebookBlockBuilder;
